import { customAlphabet } from 'nanoid'
import { VIDEO_RESOLUTION_PRESETS } from './resolutions';

/**
 * 将可能带 'Auto' 哨兵值的输入规范化为正整数，无效值返回 undefined。
 */
export const normalizeAutoNumber = (v: any): number | undefined => {
    if (typeof v === 'string' && v.trim() === 'Auto') return undefined;
    if (v === '' || v === null || typeof v === 'undefined') return undefined;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return Math.max(1, Math.ceil(n));
};

/**
 * 将可能带 'Auto' 哨兵值的输入规范化为非空字符串，无效值返回 undefined。
 */
export const normalizeAutoString = (v: any): string | undefined => {
    if (typeof v !== 'string') return undefined;
    const s = v.trim();
    if (!s || s === 'Auto') return undefined;
    return s;
};

/**
 * 按点号路径取值，如 'providerOptions.multi_shots'
 */
export function resolveDotPath(path: string, ctx: Record<string, any>): any {
    return path.split('.').reduce((o, k) => o?.[k], ctx as any);
}

/**
 * 判断 imagePrompt 是否有图像输入
 */
export function hasImageInput(imagePrompt?: string[]): boolean {
    return !!(imagePrompt && imagePrompt.length > 0);
}

/**
 * 判断 videoUri 是否有视频输入
 */
export function hasVideoInput(videoUri?: string): boolean {
    return !!videoUri;
}

/**
 * 提取首尾帧图片 URL
 * @returns [首帧URL, 尾帧URL | undefined]
 */
export function extractImageFrames(imagePrompt?: string[]): [string | undefined, string | undefined] {
    if (!imagePrompt || imagePrompt.length === 0) {
        return [undefined, undefined];
    }
    return [imagePrompt[0], imagePrompt[1]];
}

/**
 * 约束 value 的值在 allowedValues 中
 */
export function clampTo<T extends string | number>(
    value: T,
    allowedValues: readonly T[],
    defaultValue: T
): T {
    return allowedValues.includes(value) ? value : defaultValue;
}

/**
 * 生成随机种子
 */
export function generateSeed(length: number = 12): number {
    const numericNanoid = customAlphabet('0123456789', length)
    return Number(numericNanoid())
}

/**
 * 根据路径获取对象中的值
 * 支持点号与数组下标语法，例如:
 * - 'data.items[0].url'
 * - 'response[1].result.value'
 */
export function getValueByPath(obj: any, path: string): any {
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
        if (value === null || value === undefined) {
            return undefined;
        }
        // 处理数组索引，如 'items[0]'
        if (key.includes('[') && key.includes(']')) {
            const arrayKey = key.substring(0, key.indexOf('['));
            const index = parseInt(
                key.substring(key.indexOf('[') + 1, key.indexOf(']')),
                10
            );
            value = (value as any)[arrayKey];
            if (Array.isArray(value)) {
                value = value[index];
            } else {
                return undefined;
            }
        } else {
            value = (value as any)[key as any];
        }
    }
    return value;
}

/**
 * 判断状态值是否匹配（类型安全）
 * 支持 string[] 或 number[] 的状态集合
 */
export function isStatusMatch(
    state: any,
    statusValues?: string[] | number[]
): boolean {
    if (!statusValues || statusValues.length === 0) {
        return false;
    }
    if (typeof state === 'string' && typeof statusValues[0] === 'string') {
        return (statusValues as string[]).includes(state);
    }
    if (typeof state === 'number' && typeof statusValues[0] === 'number') {
        return (statusValues as number[]).includes(state);
    }
    return false;
}

/**
 * 解析多镜头提示词。
 * 格式：每行一个镜头，可选 [秒数] 前缀指定时长。
 *   [5] 一个女孩在海边奔跑
 *   [3] 镜头转向远处的灯塔
 *   [7] 夕阳西下，女孩回头微笑
 * 未标注时长的行将均分剩余时长。
 */
export function parseMultiShotPrompt(
    prompt: string,
    totalDuration: number
): { prompt: string; duration: number }[] {
    const lines = prompt.split('\n').map(l => l.trim()).filter(l => l.length > 0).slice(0, 6);
    if (lines.length === 0) {
        return [{ prompt: prompt.slice(0, 512), duration: totalDuration }];
    }

    const shots = lines.map(line => {
        const match = line.match(/^\[(\d+)]\s*(.*)/);
        return match
            ? { text: match[2].slice(0, 512), dur: parseInt(match[1], 10) }
            : { text: line.slice(0, 512), dur: 0 };
    });

    const specifiedTotal = shots.reduce((s, sh) => s + sh.dur, 0);
    const autoShots = shots.filter(s => s.dur === 0);
    const remaining = Math.max(0, totalDuration - specifiedTotal);

    if (autoShots.length > 0) {
        const perShot = Math.floor(remaining / autoShots.length);
        let extra = remaining - perShot * autoShots.length;
        for (const sh of autoShots) {
            sh.dur = Math.max(1, perShot + (extra > 0 ? 1 : 0));
            if (extra > 0) extra--;
        }
    }

    return shots.map(s => ({
        prompt: s.text,
        duration: Math.max(1, Math.min(totalDuration, s.dur)),
    }));
}

/**
 * 解析多镜头提示词，返回带 index 的数组格式（适用于 Kling 等 API）。
 * duration 以字符串形式返回。
 */
export function parseMultiShotPromptIndexed(
    prompt: string,
    totalDuration: number
): { index: number; prompt: string; duration: string }[] {
    return parseMultiShotPrompt(prompt, totalDuration).map((shot, i) => ({
        index: i + 1,
        prompt: shot.prompt,
        duration: String(shot.duration),
    }));
}

export type MultiShotDurationType = 'number' | 'string';

/**
 * 解析多镜头提示词（可配置是否带 index、duration 类型）。
 * @param prompt 多镜头文案
 * @param totalDuration 总时长（秒）
 * @param withIndex 是否在每项中包含 index（从 1 开始）
 * @param durationType duration 字段类型：'number' 为数字秒，'string' 为字符串
 */
export function parseMultiShotPromptEx(
    prompt: string,
    totalDuration: number,
    withIndex: false,
    durationType: 'number'
): { prompt: string; duration: number }[];
export function parseMultiShotPromptEx(
    prompt: string,
    totalDuration: number,
    withIndex: false,
    durationType: 'string'
): { prompt: string; duration: string }[];
export function parseMultiShotPromptEx(
    prompt: string,
    totalDuration: number,
    withIndex: true,
    durationType: 'number'
): { index: number; prompt: string; duration: number }[];
export function parseMultiShotPromptEx(
    prompt: string,
    totalDuration: number,
    withIndex: true,
    durationType: 'string'
): { index: number; prompt: string; duration: string }[];
export function parseMultiShotPromptEx(
    prompt: string,
    totalDuration: number,
    withIndex: boolean,
    durationType: MultiShotDurationType
): { prompt: string; duration: number | string }[] | { index: number; prompt: string; duration: number | string }[] {
    const shots = parseMultiShotPrompt(prompt, totalDuration);
    const asString = durationType === 'string';
    return shots.map((shot, i) => {
        const duration = asString ? String(shot.duration) : shot.duration;
        const item = { prompt: shot.prompt, duration };
        return withIndex ? { index: i + 1, ...item } : item;
    });
}

export type VideoResolutionLevel = '720p' | '1K' | '1080p';

/**
 * 规范化视频画质档位（兼容 '720P'/'1080P' 等写法）
 */
export function normalizeVideoResolutionLevel(quality?: string): VideoResolutionLevel {
	const q = (quality || '720p').toLowerCase();
	if (q === '1080p') return '1080p';
	if (q === '1k') return '1K';
	return '720p';
}

/**
 * 由“宽高比 + 画质档位”解析成以“宽*高”表示的 Size
 *
 * 720p 支持：
 * - 16:9 => 1280*720
 * - 9:16 => 720*1280
 * - 1:1  => 960*960
 * - 4:3  => 1088*832
 * - 3:4  => 832*1088
 *
 * 1080p 支持：
 * - 16:9 => 1920*1080
 * - 9:16 => 1080*1920
 * - 1:1  => 1440*1440
 * - 4:3  => 1632*1248
 * - 3:4  => 1248*1632
 *
 * 如果未命中预设，则回退到该档位的 16:9 默认尺寸。
 * 注意：当 aspectRatio 为 'match' 时无法推导输入视频比例，这里同样回退到默认比例。
 */
/**
 * Seedance 2.5 样片模式：复用原请求，强制 480p 并打开 draft。
 * 官方限制：样片只能是 480p，token 用量和单价与普通 480p 一致。
 */
export function withDraft480p<T extends { buildInput: (params: any) => any }>(config: T): T {
    return {
        ...config,
        buildInput: async (params: any) => {
            const built = await config.buildInput({ ...params, quality: '480p' });
            return { ...built, resolution: '480p', draft: true };
        },
    };
}

export function resolveVideoSize(aspectRatio?: string, quality?: string): string {
	const ratio = (aspectRatio || '16:9') as typeof VIDEO_RESOLUTION_PRESETS[number]['ratio'] | string;
	const normalizedRatio = ratio === 'match' ? '16:9' : ratio;
	const level = normalizeVideoResolutionLevel(quality);

	const preset = VIDEO_RESOLUTION_PRESETS.find((p) => p.ratio === normalizedRatio);
	const presetDim = (preset?.resolutions as Record<string, string> | undefined)?.[level];
	if (presetDim) {
		return presetDim;
	}

	if (level === '1K') return '1792*1024';
	return level === '720p' ? '1280*720' : '1920*1080';
}

